import { useMemo } from 'react';
import { useHistory, useLocation, generatePath, matchPath } from 'react-router-dom';

import {
  ROUTE_SPM_PLAN_CAPACITY_MODEL,
  ROUTE_SPM_PLAN_CAPACITY_MODEL_APPROVAL,
  ROUTE_SPM_PLAN_QUOTA_MODEL,
  ROUTE_SPM_PLAN_QUOTA_MODEL_APPROVAL,
  ROUTE_SPM_PLAN_TERRITORIES,
  ROUTE_SPM_PLAN_TERRITORIES_APPROVAL,
  ROUTE_SPM_PLAN_OVERVIEW_TAB,
  ROUTE_SPM_PLAN_DATA_TAB,
  ROUTE_SPM_PLAN_DATA_ACCOUNTS,
  ROUTE_SPM_NEW_DATA_FROM_RESOURCE,
  SPM_DESTINATIONS,
  ROUTE_SPM_PLAN_DETAIL_VIEW,
  SPMMainView,
  ROUTE_SPM_PLAN_DATA_PROPERTY,
} from 'constants/routes';
import { useSPMPlanContext } from 'contexts/SPMPlanContext';

type DESTINATION = (typeof SPM_DESTINATIONS)[keyof typeof SPM_DESTINATIONS];

type NavigationArgs = {
  destination: DESTINATION;
  routeArgs: {
    planningCycleId: string;
    scenarioId?: string;
    spmModelId?: string;
    overviewPath?: string;
    subPath?: string;
    dataPath?: string;
  };
  options?: { replace?: boolean };
};

export function useNavigation() {
  const history = useHistory();
  const location = useLocation();

  const { scenarios } = useSPMPlanContext();
  const navigateTo = ({ destination, routeArgs, options }: NavigationArgs) => {
    const path = generatePath(destination, routeArgs);
    if (path === '') {
      return;
    }

    if (options?.replace) {
      history.replace(path);
    } else {
      history.push(path);
    }
  };

  // Function to extract current tab slug from pathname
  const getCurrentTabFromPath = (path?: string): string => {
    const pathname = path || location.pathname;
    const segments = pathname.split('/');
    const lastSegment = segments[segments.length - 1];

    // Handle cases where the path might end with trailing slash or be empty
    if (!lastSegment || lastSegment === '') {
      return segments[segments.length - 2] || '';
    }

    return lastSegment;
  };

  const extractSpmModelIdFromPath = (): string | null => {
    // The model based routes (including approval review variants)
    const routes = [
      ROUTE_SPM_PLAN_TERRITORIES_APPROVAL,
      ROUTE_SPM_PLAN_QUOTA_MODEL_APPROVAL,
      ROUTE_SPM_PLAN_CAPACITY_MODEL_APPROVAL,
      ROUTE_SPM_PLAN_TERRITORIES,
      ROUTE_SPM_PLAN_QUOTA_MODEL,
      ROUTE_SPM_PLAN_CAPACITY_MODEL,
    ];

    const matchedRoute = routes
      .map((routePattern) => {
        return matchPath<{ spmModelId: string }>(location.pathname, {
          path: routePattern,
          exact: false,
        });
      })
      .find((match) => match?.params.spmModelId);

    return matchedRoute?.params.spmModelId || null;
  };

  // Determine which main panel view is currently active (ignoring subpaths)
  const currentMainView = useMemo(() => {
    // More specific routes (review variants) must come before general routes
    const routes = [
      { pattern: ROUTE_SPM_PLAN_CAPACITY_MODEL_APPROVAL, key: SPMMainView.CAPACITY },
      { pattern: ROUTE_SPM_PLAN_QUOTA_MODEL_APPROVAL, key: SPMMainView.QUOTA },
      { pattern: ROUTE_SPM_PLAN_TERRITORIES_APPROVAL, key: SPMMainView.TERRITORIES },
      { pattern: ROUTE_SPM_PLAN_OVERVIEW_TAB, key: SPMMainView.OVERVIEW },
      { pattern: ROUTE_SPM_PLAN_CAPACITY_MODEL, key: SPMMainView.CAPACITY },
      { pattern: ROUTE_SPM_PLAN_QUOTA_MODEL, key: SPMMainView.QUOTA },
      { pattern: ROUTE_SPM_PLAN_TERRITORIES, key: SPMMainView.TERRITORIES },
      { pattern: ROUTE_SPM_PLAN_DATA_TAB, key: SPMMainView.DATA },
      { pattern: ROUTE_SPM_PLAN_DATA_ACCOUNTS, key: SPMMainView.DATA_ACCOUNTS },
      { pattern: ROUTE_SPM_NEW_DATA_FROM_RESOURCE, key: SPMMainView.DATA_ACCOUNTS }, // Map to DATA_ACCOUNTS for now
      { pattern: ROUTE_SPM_PLAN_DATA_PROPERTY, key: SPMMainView.DATA_PROPERTY },
    ];

    const matchedRoute = routes.find((route) => {
      const match = matchPath(location.pathname, {
        path: route.pattern,
        exact: false,
      });
      return !!match;
    });

    return matchedRoute?.key || '';
  }, [location.pathname]) as SPMMainView | '';

  // Update the scenario ID in the current path. This also updates the territory ID according to the new scenario.
  const updateScenarioInCurrentPath = (newScenarioId: string, planningCycleId: string): void => {
    // Match against the general SPM scenario pattern
    const scenarioMatch = matchPath<{ planningCycleId: string; scenarioId: string }>(location.pathname, {
      path: `${ROUTE_SPM_PLAN_DETAIL_VIEW}/*`,
      exact: false,
    });

    if (scenarioMatch) {
      // Replace the old scenario ID with the new one in the current path
      const oldScenarioId = scenarioMatch.params.scenarioId;

      // If the path contains a territory ID, we need to update it to the new scenario's territory ID
      const territoryMatch = matchPath<{ spmModelId: string }>(location.pathname, {
        path: ROUTE_SPM_PLAN_TERRITORIES,
        exact: false,
      });

      let newPath = scenarioMatch.url.replace(`/scenarios/${oldScenarioId}`, `/scenarios/${newScenarioId}`);

      // Since each scenario is tied to a unique hierarchy model, when we switch scenarios
      // we need to invalidate and remove all cached data for the old hierarchy model.
      // This is to prevent stale data from causing issues when navigating between tabs.
      const newScenario = scenarios.find((s) => s.id === newScenarioId);
      const oldScenario = scenarios.find((s) => s.id === oldScenarioId);

      if (newScenario?.hierarchy_model?.id && oldScenario?.hierarchy_model?.id) {
        const newTerritoryId = newScenario.hierarchy_model.id;
        const oldTerritoryId = oldScenario.hierarchy_model.id;

        // Update the territory ID in the path if currently on territories route
        if (territoryMatch) {
          newPath = newPath.replace(`/territories/${oldTerritoryId}`, `/territories/${newTerritoryId}`);
        }
      }
      history.push(newPath);
    } else {
      // Fallback, default to overview
      const overviewPath = generatePath(ROUTE_SPM_PLAN_OVERVIEW_TAB, {
        planningCycleId,
        scenarioId: newScenarioId,
        overviewPath: 'dashboard',
      });
      history.push(overviewPath);
    }
  };

  return {
    navigateTo,
    getCurrentTabFromPath,
    extractSpmModelIdFromPath,
    currentMainView,
    updateScenarioInCurrentPath,
    DESTINATIONS: SPM_DESTINATIONS,
  };
}
